import React from 'react';
import type { FC, ReactNode } from 'react';

import { gql } from '@apollo/client';
import type { SupportedLanguage } from '@modernloop/i18n';
import { SupportedLocalesContext } from '@modernloop/shared/feature-flag';

import { toSupportedLanguages } from 'src/contexts/supportedLocales';

import { useInterviewerSupportedLocalesQuery } from 'src/interviewer-portal/generated/graphql';

// Defined against the interviewer schema so the generated hook + operation belong to the
// interviewer GraphQL client, not the recruiting one. Reusing the recruiting hook here would run a
// recruiting-schema operation over /graphql/interviewer/, which we want to avoid.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InterviewerSupportedLocalesGqlQuery = gql`
  query InterviewerSupportedLocales {
    thisOrg {
      id
      supportedLocales
    }
  }
`;

interface InterviewerSupportedLocalesProviderProps {
  children?: ReactNode;
}

/**
 * Interviewer-portal counterpart of SupportedLocalesProvider. Resolves the org's enabled locales
 * via the interviewer schema/client and feeds the shared SupportedLocalesContext.
 */
export const InterviewerSupportedLocalesProvider: FC<InterviewerSupportedLocalesProviderProps> = ({ children }) => {
  const { data } = useInterviewerSupportedLocalesQuery();

  const value: SupportedLanguage[] = React.useMemo(() => toSupportedLanguages(data?.thisOrg?.supportedLocales), [data]);

  return <SupportedLocalesContext.Provider value={value}>{children}</SupportedLocalesContext.Provider>;
};
